import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { query, collection, orderBy, onSnapshot } from 'firebase/firestore';

// ◄ CORRECCIÓN: Rutas relativas de Firebase y useAuth recalibradas con precisión
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth';
import { getPersonaChipClass, getSiniestroStatusChip } from '../../../../utils/styles.js';

// NUESTRO ARSENAL UNIFICADO DE ARQUITECTURA
import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// MODALES EXISTENTES EN EL PROYECTO
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';
import ModalFichaCliente from '../../../ui/ModalFichaCliente.jsx';
import ModalConfirmacion from '../../../ui/ModalConfirmacion.jsx';
import ModalSiniestro from '../../../ui/ModalSiniestro.jsx'; 
import ModalNotificacion from '../../../ui/ModalNotificacion.jsx';

// FILTROS Y CONTROLLER DE NEGOCIO
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import { useSiniestrosTable } from './useSiniestrosTable';

// --- SUB-COMPONENTE INTERNO MEMOIZADO PARA SEGUIMIENTO ---
const ClaimsHistory = memo(({ path }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!path) return;
        const q = query(collection(db, `${path}/gestiones`), orderBy('fechaCreacion', 'desc'));
        return onSnapshot(q, (snap) => {
            setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        });
    }, [path]);

    if (loading) return <div className="py-4 text-center text-[10px] font-bold text-slate-400 animate-pulse uppercase tracking-widest">Sincronizando seguimiento...</div>;

    return (
        <div className="space-y-3 text-left">
            <h4 className="text-[10px] font-black text-blue-800 uppercase tracking-widest flex items-center gap-2">
                <Icons.History className="w-3 h-3 text-blue-600" /> Seguimiento del Trámite
            </h4>
            <div className="grid grid-cols-1 gap-2 text-left">
                {history.length > 0 ? history.map((h, i) => (
                    <div key={i} className="flex items-center gap-4 bg-white p-3 rounded-xl border border-slate-100 shadow-sm transition-all hover:border-blue-200 group text-left">
                        <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-lg tabular-nums">
                            {h.fechaCreacion?.seconds 
                                ? new Date(h.fechaCreacion.seconds * 1000).toLocaleDateString('es-AR') 
                                : (h.fechaCreacion instanceof Date ? h.fechaCreacion.toLocaleDateString('es-AR') : '-')}
                        </span>
                        <span className="text-[10px] font-black text-slate-800 uppercase min-w-[120px]">{h.tipo || h.tipoGestion}</span>
                        <p className="text-xs text-slate-500 italic flex-grow">"{h.observacion}"</p>
                        <span className="text-[9px] font-bold text-slate-300 uppercase group-hover:text-slate-400 transition-colors">{h.usuarioNombre || h.usuario || 'Sistema'}</span>
                    </div>
                )) : (
                    <div className="p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200 text-center w-full">
                        <p className="text-xs text-slate-400 italic">No se han registrado movimientos para este siniestro.</p>
                    </div>
                )}
            </div>
        </div>
    );
});
ClaimsHistory.displayName = 'ClaimsHistory';

// --- FILA MEMOIZADA ---
const SiniestroRow = memo(({ item, isLocked, isLockedByMe, isExpanded, columnVisibility, onToggleExpand, onAction, onUnlock, user }) => {
    const activeColumnsCount = useMemo(() => {
        return Object.values(columnVisibility).filter(v => v).length + 2;
    }, [columnVisibility]);

    return (
        <>
            <tr 
                onClick={() => !isLocked && onToggleExpand(item.id)} 
                className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                    ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''}
                    ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
            >
                <td className={`p-2.5 w-12 text-center border-b border-r border-slate-100 sticky left-0 z-[45] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                    <div className={`w-3.5 h-3.5 mx-auto transition-transform duration-300 ${isExpanded ? 'rotate-90 text-red-600' : 'text-slate-300'}`}>
                        <Icons.ChevronRight />
                    </div>
                </td>
                
                {columnVisibility.fechaCarga && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-slate-500 tabular-nums">
                        {item.fechaCarga?.split('-').reverse().join('/') || '-'}
                    </td>
                )}
                {columnVisibility.fechaSiniestro && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-red-700 tabular-nums bg-red-50/20">
                        {item.fechaSiniestro?.split('-').reverse().join('/') || '-'}
                    </td>
                )}
                {columnVisibility.displayNombre && (
                    <td className="p-2.5 px-4 text-sm text-slate-900 font-bold border-b border-r border-slate-100 truncate max-w-[280px]">
                        {item.displayNombre}
                    </td>
                )}
                {columnVisibility.displayDoc && <td className="p-2.5 px-3 text-sm text-slate-600 font-mono text-center border-b border-r border-slate-100">{item.displayDoc}</td>}
                {columnVisibility.nPoliza && <td className="p-2.5 px-3 text-sm text-slate-900 font-bold text-center border-b border-r border-slate-100 tabular-nums">{item.nPoliza || '-'}</td>}
                
                {columnVisibility.compania && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                        <div className="flex items-center justify-center">
                            <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg border border-blue-100 text-[10px] font-black uppercase shadow-sm">
                                {item.compania || '-'}
                            </span>
                        </div>
                    </td>
                )}
                {columnVisibility.riesgo && (
                    <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                        <div className="flex flex-col text-left">
                            <span className="font-bold text-slate-800 tracking-tight">{item.riesgo || '-'}</span>
                            <span className="text-[11px] font-medium text-slate-500 uppercase">{item.subriesgo || 'General'}</span>
                        </div>
                    </td>
                )}
                {columnVisibility.tipoSiniestro && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-[10px] font-black uppercase text-red-600 bg-red-50/30">
                        {item.tipoSiniestro || '-'}
                    </td>
                )}
                {columnVisibility.quienGestiono && (
                    <td className="p-2.5 px-3 text-center border-b border-r border-slate-100">
                        {item.quienGestiono ? (
                            <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(item.quienGestiono)}`}>
                                <span className="text-[10px] font-bold uppercase">{item.quienGestiono}</span>
                            </div>
                        ) : '-'}
                    </td>
                )}
                {columnVisibility.numeroSiniestro && (
                    <td className="p-2.5 px-3 text-sm text-slate-600 font-mono text-center border-b border-r border-slate-100 font-bold">{item.numeroSiniestro || '-'}</td>
                )}
                {columnVisibility.estadoGestion && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase border ${getSiniestroStatusChip((item.estadoGestion || 'Abierto').toUpperCase())}`}>
                            {item.estadoGestion || 'Abierto'}
                        </span>
                    </td>
                )}
                {columnVisibility.canal && (
                    <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                        <div className="flex flex-col text-left">
                            <span className="font-bold text-slate-800 tracking-tight">{item.canal || '-'}</span>
                            <span className="text-[11px] font-medium text-slate-500 uppercase">{item.displayCanalNombre || '-'}</span>
                        </div>
                    </td>
                )}

                {/* Acciones */}
                <td className="p-2.5 px-4 text-right sticky right-0 transition-colors z-20 border-b border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)] bg-white group-hover:bg-slate-50">
                    <div className="flex justify-end items-center gap-1.5">
                        <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'poliza'); }} className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all" title="Ver Póliza"><Icons.Policy /></button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'ficha'); }} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Ficha Cliente"><Icons.User /></button>
                        
                        {isLocked ? (
                            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 animate-pulse shadow-sm">
                                <Icons.Lock /> <span className="text-[9px] font-black uppercase tracking-tighter">{item.bloqueo?.userName || 'Ocupado'}</span>
                            </div>
                        ) : (
                            <>
                                <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'edit'); }} className="p-1.5 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Editar Siniestro"><Icons.Edit /></button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'delete'); }} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Eliminar Siniestro"><Icons.Trash /></button>
                                {isLockedByMe && (
                                    <button onClick={(e) => { e.stopPropagation(); onUnlock(item.path); }} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar Caso">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </td>
            </tr>

            {isExpanded && !isLocked && (
                <tr className="bg-slate-50/80 animate-fade-in border-b border-slate-200 shadow-inner">
                    <td colSpan={activeColumnsCount} className="p-0 overflow-hidden relative text-left">
                        <div className="sticky left-0 w-[calc(100vw-120px)] p-8 md:p-12 text-left text-slate-900">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 text-left">
                                <div className="space-y-4 text-left">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-left">Observaciones Iniciales</h4>
                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm min-h-[80px]">
                                        <p className="text-sm text-slate-600 italic leading-relaxed text-left">
                                            {item.detalleSiniestro || "No se registraron observaciones detalladas."}
                                        </p>
                                    </div>
                                </div>
                                <ClaimsHistory path={item.path} />
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
});
SiniestroRow.displayName = 'SiniestroRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const GestionSiniestros = () => {
    const navigate = useNavigate();
    
    // ◄ CORRECCIÓN ESLINT: Importamos y desestructuramos useAuth() directamente en la vista
    const { user } = useAuth();

    // Consumimos nuestro cerebro estructurado
    const {
        loading,
        clientsCache,
        activeItem,
        activePolizaData,
        showEditModal,
        showPolicyModal,
        showFichaModal,
        showDeleteConfirm,
        alertConfig,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowEditModal,
        setShowPolicyModal,
        setShowFichaModal,
        setShowDeleteConfirm,
        setAlertConfig,
        setActiveItem,
        setActivePolizaData,
        handleAction,
        handleSuccess,
        confirmDelete,
        unlockPoliza
    } = useSiniestrosTable();

    // Desestructuramos el estado síncrono unificado para inyectarlo al HTML
    // ◄ CORRECCIÓN ESLINT: Desestructuramos también 'setCurrentPage' de tableState
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        toggleRowExpansion,
        expandedRowId,
        checkIsLocked: checkIsItemLocked
    } = tableState;

    const columnLabels = {
        fechaCarga: "Carga", fechaSiniestro: "Siniestro", displayNombre: "Cliente",
        displayDoc: "DNI/CUIT", nPoliza: "Póliza", compania: "Compañía", riesgo: "Riesgo/Sub",
        tipoSiniestro: "Tipo", quienGestiono: "Responsable", numeroSiniestro: "N° Siniestro",
        estadoGestion: "Estado", canal: "Canal"
    };

    // Liberación por descarga involuntaria
    useEffect(() => {
        const handleUnload = () => { if (activeItemPathRef.current) unlockPoliza(activeItemPathRef.current); };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [unlockPoliza, activeItemPathRef]);

    // Configuración de Filtros Dinámicos
    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        const filteredByRiesgo = filterValues.riesgo?.length > 0 ? itemsTable.filter(i => filterValues.riesgo.includes(i.riesgo)) : itemsTable;
        const filteredByCanal = filterValues.canal?.length > 0 ? itemsTable.filter(i => filterValues.canal.includes(i.canal)) : itemsTable;
        
        return [
            { name: 'search', label: 'Buscador Global', type: 'text', placeholder: 'Cliente, Póliza, Siniestro...', className: 'w-80' },
            { name: 'riesgo', label: 'Riesgo', type: 'select', isMulti: true, options: getUnique('riesgo') },
            { name: 'tipoSiniestro', label: 'Tipo Siniestro', type: 'select', isMulti: true, options: getUnique('tipoSiniestro', filteredByRiesgo) },
            { name: 'compania', label: 'Compañía', type: 'select', isMulti: true, options: getUnique('compania') },
            { name: 'quienGestiono', label: 'Gestionó', type: 'select', isMulti: true, options: getUnique('quienGestiono') },
            { name: 'estadoGestion', label: 'Estado', type: 'select', isMulti: true, options: getUnique('estadoGestion') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: getUnique('displayCanalNombre', filteredByCanal) },
            { name: 'fechaGestion', label: 'Fecha Gestión', type: 'date-range' },
            { name: 'fechaSiniestro', label: 'Fecha Siniestro', type: 'date-range' },
        ];
    }, [itemsTable, filterValues]);

    if (loading) return (
        <div className="flex h-screen flex-col items-center justify-center bg-white space-y-4">
            <div className="w-12 h-12 border-4 border-red-600/20 border-t-red-600 rounded-full animate-spin"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-red-600">Sincronizando Tablero</p>
        </div>
    );

    return (
        <div className="flex h-screen w-full flex-col overflow-hidden bg-white font-['Inter'] antialiased text-slate-900 text-left">
            <div className="h-1 w-full bg-red-600 shrink-0"></div>
            
            {/* CABECERA UNIFICADA (z-[110] soluciona el solapamiento con los dropdowns flotantes) */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button type="button" onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><Icons.ArrowLeft /></button>
                    <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Gestión de Siniestros</h2>
                </div>
                <div className="flex items-center gap-4">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm">
                        <div className="bg-red-600 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Casos Activos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS CON KEY ESTABLE */}
            <FiltrosDinamicos 
                key="siniestros-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA CONTENEDORA COMÚN */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="3200px">
                    <thead>
                        <tr className="bg-slate-900 text-white">
                            <th className="w-8 border-b border-r border-slate-700 bg-slate-900 sticky left-0 z-[60] top-0 shadow-[2px_0_5px_rgba(0,0,0,0.2)]"></th>
                            <SortableHeader label="Carga" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-20 text-center" />
                            <SortableHeader label="Siniestro" sortKey="fechaSiniestro" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-20 text-center" />
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[200px]" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="N° Póliza" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Riesgo / Sub" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[150px]" />
                            <SortableHeader label="Tipo" sortKey="tipoSiniestro" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-center" />
                            <SortableHeader label="Responsable" sortKey="quienGestiono" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="N° Siniestro" sortKey="numeroSiniestro" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="Estado" sortKey="estadoGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="Canal" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28" />
                            <th className="w-24 p-4 sticky right-0 top-0 bg-slate-900 border-b border-slate-700 z-[65] uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {paginatedData.map(item => {
                            const isLocked = checkIsItemLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <SiniestroRow 
                                    key={item.id} 
                                    item={item} 
                                    isLocked={isLocked}
                                    isLockedByMe={isLockedByMe}
                                    isExpanded={expandedRowId === item.id}
                                    columnVisibility={columnVisibility}
                                    onToggleExpand={toggleRowExpansion}
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza}
                                    user={user}
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN COMÚN */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="siniestros"
                    accentClass="text-red-700 bg-red-50 border-red-100"
                />
            </main>

            {/* MODALES RECONECTADOS */}
            {activeItem && (
                <>
                    <ModalFichaCliente 
                        show={showFichaModal} 
                        onClose={() => { setShowFichaModal(false); setActiveItem(null); }} 
                        client={clientsCache[activeItem.clienteId] || { id: activeItem.clienteId }} 
                    />
                    <ModalDetallePoliza 
                        show={showPolicyModal} 
                        onClose={() => { setShowPolicyModal(false); setActivePolizaData(null); setActiveItem(null); }} 
                        poliza={activePolizaData} 
                    />
                    <ModalSiniestro 
                        show={showEditModal} 
                        onClose={() => { if (activeItem) unlockPoliza(activeItem.path); setShowEditModal(false); setActiveItem(null); setActivePolizaData(null); }} 
                        onSuccess={handleSuccess}
                        initialData={activeItem} 
                        poliza={activePolizaData}
                        clientData={clientsCache[activeItem.clienteId] || { id: activeItem.clienteId }}
                    />
                    <ModalConfirmacion 
                        show={showDeleteConfirm} 
                        onClose={() => { setShowDeleteConfirm(false); setActiveItem(null); }} 
                        onConfirm={confirmDelete} 
                        title="Eliminar Siniestro" 
                        message="¿Estás seguro de eliminar este siniestro? Esta acción es irreversible." 
                    />
                </>
            )}

            <ModalNotificacion 
                show={alertConfig.show}
                onClose={() => setAlertConfig({ ...alertConfig, show: false })}
                title={alertConfig.title}
                message={alertConfig.message}
                type={alertConfig.type}
            />
        </div>
    );
};

export default GestionSiniestros;